'use client';

import { asLink, asText, Content, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { useCardCarousel } from '@/hooks/useCardCarousel';
import type {
  AuthorDocument,
  EbookDocument,
} from '../../../../prismicio-types';
import { EbookCarouselCard } from './components/EbookCarouselCard';

type CarouselSlice = Extract<
  Content.FeaturedPostsSlice,
  { variation: 'expertsInPlaybooksCarousel' }
>;

interface Props {
  slice: CarouselSlice;
  ebooks: EbookDocument[];
  devsuAuthor: AuthorDocument | null;
}

type EbookAuthorData = {
  uid?: string;
  data?: {
    name?: string;
    avatar?: import('@prismicio/client').ImageFieldImage;
    bio?: { type: string; text: string }[];
  };
};

/**
 * Carousel presentation of Experts in Playbooks: same server-fetched ebooks
 * (with Devsu-author fallback) as the classic ExpertsInPlaybooks variant, but
 * laid out as ProcessOverviewThreeStep's redesignCarousel — dark section, a
 * horizontal card rail, each card pairs a content column with an image
 * (instead of that slice's video slot).
 */
export default function ExpertsInPlaybooksCarousel({
  slice,
  ebooks,
  devsuAuthor,
}: Props) {
  const { subtitle, title, theme, bg_image } = slice.primary;
  const { carouselRef, scrollEdge, scrollByCard } = useCardCarousel();

  // `theme` is a Select — default to "dark" so the current look is preserved
  // when an editor hasn't picked one yet. Only the subtitle color flips with
  // the theme; the title always uses the accent green.
  const isLight = theme === 'light';
  const sectionBgClass = isLight ? 'bg-white' : 'bg-[#262626]';
  const subtitleColorClass = isLight ? 'text-[#041118]' : 'text-white';
  const headingSizeClass =
    'text-[28px] font-medium leading-[1.2] md:text-[42px]';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative overflow-hidden py-16 md:py-20 lg:py-[70px] ${sectionBgClass}`}
    >
      {isFilled.image(bg_image) && (
        <PrismicNextImage
          field={bg_image}
          fill
          sizes="100vw"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          fallbackAlt=""
        />
      )}

      <div className="relative flex flex-col gap-9">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col px-5 md:px-[30px]">
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className={`${headingSizeClass} text-[#05f273]`}>
                    {children}
                  </h2>
                ),
              }}
            />
          )}
          {isFilled.richText(subtitle) && (
            <PrismicRichText
              field={subtitle}
              components={{
                heading3: ({ children }) => (
                  <h3 className={`${headingSizeClass} ${subtitleColorClass}`}>
                    {children}
                  </h3>
                ),
              }}
            />
          )}
        </div>

        <div className="relative">
          <div
            ref={carouselRef}
            className="flex w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div
              aria-hidden
              className="w-5 shrink-0 md:w-[max(30px,calc((100%-1280px)/2+30px))]"
            />
            {ebooks.map((ebook, index) => {
              const { data, uid } = ebook;
              const href =
                asLink(data.link_to_landing_page) || `/ebooks/${uid}`;
              const ebookTitle = asText(data.name);

              const linkedAuthor = data.author as unknown as EbookAuthorData;
              const hasLinkedAuthor = Boolean(linkedAuthor?.data);
              const authorName =
                linkedAuthor?.data?.name || devsuAuthor?.data?.name || 'Devsu';
              const linkedBio = linkedAuthor?.data?.bio
                ? asText(linkedAuthor.data.bio as never)
                : null;
              const fallbackBio = devsuAuthor?.data?.bio
                ? asText(devsuAuthor.data.bio)
                : null;
              const authorBio = linkedBio || fallbackBio;
              const authorAvatar = hasLinkedAuthor
                ? (linkedAuthor?.data?.avatar ?? null)
                : (devsuAuthor?.data?.avatar ?? null);
              const authorUid =
                linkedAuthor?.uid || devsuAuthor?.uid || 'devsu';

              return (
                <div
                  key={ebook.id}
                  data-phase-card
                  className={`flex w-[92%] shrink-0 flex-col overflow-hidden rounded-[20px] sm:w-[85%] md:items-stretch lg:w-[min(1280px,calc(100vw-60px))] lg:flex-row xl:w-[1280px] ${
                    index === 0 ? '' : 'ml-9'
                  }`}
                >
                  <EbookCarouselCard
                    href={href}
                    title={ebookTitle}
                    image={isFilled.image(data.image) ? data.image : null}
                    author={{
                      name: authorName,
                      href: `/resources-center/authors/${authorUid}`,
                      avatar: authorAvatar ?? null,
                      bio: authorBio,
                    }}
                    atStart={scrollEdge.atStart}
                    atEnd={scrollEdge.atEnd}
                    onPrev={() => scrollByCard(-1)}
                    onNext={() => scrollByCard(1)}
                  />
                </div>
              );
            })}
            <div
              aria-hidden
              className="w-5 shrink-0 md:w-[max(30px,calc((100%-1280px)/2+30px))]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
